import { LogService } from 'library/ts/utilities/logger/LogService';
import * as Type from 'library/ts/utilities/modules/Type';
const KeySystem = {
    widevine: ['com.widevine.alpha'],
    playready: ['com.microsoft.playready', 'com.youtube.playready'],
    clearkey: ['webkit-org.w3.clearkey', 'org.w3.clearkey'],
    primetime: ['com.adobe.primetime', 'com.adobe.access'],
    fairplay: ['com.apple.fairplay']
};
export var Robustness;
(function (Robustness) {
    Robustness["SW_SECURE_CRYPTO"] = "SW_SECURE_CRYPTO";
    Robustness["SW_SECURE_DECODE"] = "SW_SECURE_DECODE";
    Robustness["HW_SECURE_CRYPTO"] = "HW_SECURE_CRYPTO";
    Robustness["HW_SECURE_DECODE"] = "HW_SECURE_DECODE";
    Robustness["HW_SECURE_ALL"] = "HW_SECURE_ALL";
})(Robustness || (Robustness = {}));
export var VideoCodec;
(function (VideoCodec) {
    VideoCodec["AVC_BASELINE_L3"] = "video/mp4; codecs=\"avc1.42001e\"";
    VideoCodec["AVC_MAIN_L3_1"] = "video/mp4; codecs=\"avc1.4d001f\"";
    VideoCodec["AVC_HIGH_L4"] = "video/mp4; codecs=\"avc1.640028\"";
    VideoCodec["H264_HIGH_L4"] = "video/mp4; codecs=\"avc1.640028\"";
})(VideoCodec || (VideoCodec = {}));
const HDCPVersions = [
    '1.0',
    '1.1',
    '1.2',
    '1.3',
    '1.4',
    '2.0',
    '2.1',
    '2.2',
    '2.3'
];
const getUseCases = () => {
    return [
        {
            robustness: Robustness.SW_SECURE_CRYPTO,
            hdcpVersions: HDCPVersions
        },
        {
            robustness: Robustness.SW_SECURE_DECODE,
            hdcpVersions: HDCPVersions
        },
        {
            robustness: Robustness.HW_SECURE_CRYPTO,
            hdcpVersions: HDCPVersions
        },
        {
            robustness: Robustness.HW_SECURE_DECODE,
            hdcpVersions: HDCPVersions
        },
        {
            robustness: Robustness.HW_SECURE_ALL,
            hdcpVersions: HDCPVersions
        }
    ];
};
const testHDCP = (hdcpTest) => {
    const testLogger = LogService.create({ prefixes: ['SmartTag:', 'testHDCP:'] });
    return new Promise((resolve, reject) => {
        navigator.requestMediaKeySystemAccess(hdcpTest.keySystem, hdcpTest.mediaKeysSystemConfig)
            .then((mediaKeySystemAccess) => {
            mediaKeySystemAccess.createMediaKeys()
                .then((mediaKeys) => {
                testLogger.log('Getting HDCP status...', 'mediaKeys:', mediaKeys);
                if (Type.isFunction(mediaKeys['getStatusForPolicy']) === false) {
                    hdcpTest.supported = false;
                    reject({ error: true, message: 'HDCP Policy Check API is not available.', data: hdcpTest });
                }
                return mediaKeys['getStatusForPolicy']({ minHdcpVersion: hdcpTest.hdcpVersion });
            })
                .then((status) => {
                if (status !== 'usable') {
                    testLogger.log('HDCP is not available for ' + hdcpTest.hdcpVersion);
                    hdcpTest.supported = false;
                    reject({ error: true, message: 'status:' + status, data: hdcpTest });
                }
                testLogger.log('> HDCP is available for ' + hdcpTest.hdcpVersion, 'status:', status);
                hdcpTest.supported = true;
                resolve({ error: false, message: 'HDCP is available for ' + hdcpTest.hdcpVersion, data: hdcpTest });
            })
                .catch((error) => {
                testLogger.log('Error:', error);
                reject({ error: true, message: error.message, data: error });
            });
        })
            .catch((error) => {
            testLogger.log('requestMediaKeySystemAccess:', 'Error:', error);
            reject({ error: true, message: error.message, data: error });
        });
    });
};
export const support = (videoCodec) => {
    const supportLogger = LogService.create({ noTimestamp: true });
    let useCaseIndex = 0;
    let hdcpVersionIndex = 0;
    const useCases = getUseCases();
    const supported = {
        videoCodec: videoCodec,
        widevine: {
            hdcpVersion: undefined,
            robustness: undefined
        }
    };
    const getHDCPTestConfig = (videoCodec, useCaseIndex, hdcpVersionIndex) => {
        return {
            mediaKeysSystemConfig: [
                {
                    videoCapabilities: [
                        {
                            contentType: videoCodec,
                            robustness: useCases[useCaseIndex].robustness
                        }
                    ]
                }
            ],
            hdcpVersion: useCases[useCaseIndex].hdcpVersions[hdcpVersionIndex],
            keySystem: KeySystem.widevine[0].toString().trim(),
            supported: false
        };
    };
    return new Promise((resolve, reject) => {
        if (Type.isEmpty(videoCodec))
            reject({ error: true, message: 'ERROR: videoCodec is not defined!' });
        const done = () => {
            supportLogger.log('%cSmartTag: ' + `SUPPORTED: HDCP (widevine): ${supported.widevine.hdcpVersion}, ROBUSTNESS: ${supported.widevine.robustness}`, 'color: green; font-weight: bold;');
            resolve(supported);
        };
        const hdcpTestExecutor = (testConfig) => {
            testHDCP(testConfig)
                .then((data) => {
                const hdcpVersion = parseFloat(data.data.hdcpVersion);
                const robustness = data.data.mediaKeysSystemConfig[0].videoCapabilities[0].robustness;
                supportLogger.log('%cSmartTag: Result:', 'color: blue;', 'Robustness:', robustness, 'HDCP:', hdcpVersion, 'Supported:', data.data.supported);
                supported.widevine.hdcpVersion = hdcpVersion;
                supported.widevine.robustness = robustness;
                if (useCaseIndex < (useCases.length - 1)) {
                    if (hdcpVersionIndex < (HDCPVersions.length - 1)) {
                        hdcpVersionIndex++;
                        hdcpTestExecutor(getHDCPTestConfig(videoCodec, useCaseIndex, hdcpVersionIndex));
                    }
                    else {
                        useCaseIndex++;
                        hdcpVersionIndex = 0;
                        hdcpTestExecutor(getHDCPTestConfig(videoCodec, useCaseIndex, hdcpVersionIndex));
                    }
                }
                else {
                    if (hdcpVersionIndex < (HDCPVersions.length - 1)) {
                        hdcpVersionIndex++;
                        hdcpTestExecutor(getHDCPTestConfig(videoCodec, useCaseIndex, hdcpVersionIndex));
                    }
                    else {
                        supportLogger.log('%cSmartTag: Use Cases Done.', 'color: green;');
                        done();
                    }
                }
            })
                .catch((error) => {
                const failedTest = getHDCPTestConfig(videoCodec, useCaseIndex, hdcpVersionIndex);
                const hdcpVersion = parseFloat(failedTest.hdcpVersion);
                const robustness = failedTest.mediaKeysSystemConfig[0].videoCapabilities[0].robustness;
                supportLogger.log('%cSmartTag: STOP further tests:', 'color: red; font-weight: bold;', '\nFailed result:', `Robustness: ${robustness}`, 'HDCP:', hdcpVersion, 'Supported:', failedTest.supported);
                supportLogger.log('%cSmartTag: Done due to error:', 'color: red; font-weight: bold;', error);
                done();
            });
        };
        supportLogger.log('%cSmartTag: Start HDCP tests...', 'color: green;');
        hdcpTestExecutor(getHDCPTestConfig(videoCodec, useCaseIndex, hdcpVersionIndex));
    });
};
